import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DestructiveConfirm } from '@/components/ui/DestructiveConfirm'
import { SkeletonCard } from '@/components/ui/SkeletonLoader'
import { BRAND_MINT } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'
import {
  getAdminUsersApi,
  getAdminUserByIdApi,
  updateAdminUserApi,
  getUserSessionsApi,
  revokeUserSessionApi,
  deleteAdminUserApi,
  getAdminUserLoginHistoryApi,
  type AdminUser,
  type UserSession,
  type UserDevice,
  type LoginHistoryEntry,
} from '@/lib/api'
import {
  LoaderIcon,
  UsersIcon,
  SearchIcon,
  FilterIcon,
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MailIcon,
  BuildingIcon,
  HashIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  ShieldIcon,
  ClockIcon,
  BanIcon,
  AlertTriangleIcon,
  SmartphoneIcon,
  GlobeIcon,
  ActivityIcon,
  LogOutIcon,
  Trash2Icon,
  DownloadIcon,
  CheckSquareIcon,
  SquareIcon,
} from 'lucide-react'

/**
 * Color treatment for each user role badge.
 */
const ROLE_STYLES: Record<string, { label: string; color: string }> = {
  super_admin: {
    label: 'Super Admin',
    color: 'bg-red-500/10 text-red-600 dark:text-red-400',
  },
  admin: {
    label: 'Admin',
    color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  },
  company_admin: {
    label: 'Company Admin',
    color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  },
  hr_manager: {
    label: 'HR Manager',
    color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  },
  payroll_admin: {
    label: 'Payroll Admin',
    color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  },
  employee: {
    label: 'Employee',
    color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  },
}

const ROLE_OPTIONS = [
  'employee',
  'company_admin',
  'hr_manager',
  'payroll_admin',
  'admin',
  'super_admin',
] as const

function getRoleStyle(role: string) {
  return ROLE_STYLES[role] ?? {
    label: role.replace(/_/g, ' '),
    color: 'bg-muted text-muted-foreground',
  }
}

/**
 * Format a timestamp for display.
 */
function formatTimestamp(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export const Route = createFileRoute('/dashboard/admin/users')({
  component: () => {
    const [users, setUsers] = useState<AdminUser[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [limit] = useState(50)
    const [loading, setLoading] = useState(true)

    // Filter state
    const [search, setSearch] = useState('')
    const [searchInput, setSearchInput] = useState('')
    const debouncedSearchInput = useDebounce(searchInput, 300)
    const [roleFilter, setRoleFilter] = useState('')
    const [orgIdFilter, setOrgIdFilter] = useState('')
    const [showFilters, setShowFilters] = useState(false)

    // Auto-search when debounced value changes
    useEffect(() => {
      setSearch(debouncedSearchInput.trim())
      setPage(1)
    }, [debouncedSearchInput])

    // Detail drawer
    const [detailUser, setDetailUser] = useState<AdminUser | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [showDetail, setShowDetail] = useState(false)

    // Role management
    const [roleDraft, setRoleDraft] = useState('')
    const [roleSaving, setRoleSaving] = useState(false)

    // Suspension
    const [suspending, setSuspending] = useState(false)
    const [confirmSuspend, setConfirmSuspend] = useState(false)

    // GDPR Delete
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)

    // Sessions
    const [sessions, setSessions] = useState<UserSession[]>([])
    const [devices, setDevices] = useState<UserDevice[]>([])
    const [sessionsLoading, setSessionsLoading] = useState(false)
    const [activeSessions, setActiveSessions] = useState(0)
    const [revokingSessionId, setRevokingSessionId] = useState<number | null>(null)

    // Login history
    const [loginHistory, setLoginHistory] = useState<LoginHistoryEntry[]>([])
    const [loginHistoryLoading, setLoginHistoryLoading] = useState(false)

    // Bulk selection
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
    const [bulkSuspending, setBulkSuspending] = useState(false)
    const [bulkDeleting, setBulkDeleting] = useState(false)
    const [confirmBulkSuspend, setConfirmBulkSuspend] = useState(false)
    const [confirmBulkDelete, setConfirmBulkDelete] = useState(false)

    const toggleSelect = (id: number) => {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        next.has(id) ? next.delete(id) : next.add(id)
        return next
      })
    }

    const toggleSelectAll = () => {
      if (selectedIds.size === users.length) {
        setSelectedIds(new Set())
      } else {
        setSelectedIds(new Set(users.map((u) => u.user_id)))
      }
    }

    const clearSelection = () => setSelectedIds(new Set())

    // Clear selection when page or filters change (stale selections cause confusing bulk action behavior)
    useEffect(() => { clearSelection() }, [page, search, roleFilter, orgIdFilter])

    const handleBulkSuspend = async () => {
      setConfirmBulkSuspend(false)
      setBulkSuspending(true)
      const ids = [...selectedIds]
      const results = await Promise.allSettled(
        ids.map((id) => updateAdminUserApi(id, { suspended: true })),
      )
      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed === 0) {
        toast.success(`${succeeded} user${succeeded !== 1 ? 's' : ''} suspended.`)
      } else {
        toast.warning(`${succeeded} suspended, ${failed} failed.`)
      }
      setUsers((prev) => prev.map((u) => selectedIds.has(u.user_id) ? { ...u, suspended_at: new Date().toISOString() } : u))
      clearSelection()
      setBulkSuspending(false)
    }

    const handleBulkDelete = async () => {
      setConfirmBulkDelete(false)
      setBulkDeleting(true)
      const ids = [...selectedIds]
      const results = await Promise.allSettled(
        ids.map((id) => deleteAdminUserApi(id)),
      )
      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed === 0) {
        toast.success(`${succeeded} user${succeeded !== 1 ? 's' : ''} deleted.`)
      } else {
        toast.warning(`${succeeded} deleted, ${failed} failed.`)
      }
      setUsers((prev) => prev.filter((u) => !selectedIds.has(u.user_id)))
      setTotal((prev) => Math.max(0, prev - succeeded))
      clearSelection()
      setBulkDeleting(false)
    }

    const totalPages = Math.max(1, Math.ceil(total / limit))

    const buildParams = useCallback(() => {
      const params: Record<string, string | number> = { page, limit }
      if (search) params.search = search
      if (roleFilter) params.role = roleFilter
      const orgId = parseInt(orgIdFilter, 10)
      if (!isNaN(orgId) && orgId > 0) params.org_id = orgId
      return params
    }, [page, limit, search, roleFilter, orgIdFilter])

    useEffect(() => {
      let cancelled = false

      async function load() {
        setLoading(true)
        try {
          const res = await getAdminUsersApi(buildParams())
          if (!cancelled) {
            setUsers(res.data)
            setTotal(res.total ?? 0)
          }
        } catch (err) {
          if (!cancelled) {
            toast.error(
              err instanceof Error ? err.message : 'Failed to load users',
            )
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }

      load()
      return () => { cancelled = true }
    }, [buildParams])

    const handleSearch = () => {
      setSearch(searchInput.trim())
      setPage(1)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch()
    }

    const clearFilters = () => {
      setSearchInput('')
      setSearch('')
      setRoleFilter('')
      setOrgIdFilter('')
      setPage(1)
    }

    const hasFilters = !!(search || roleFilter || orgIdFilter)

    const exportCSV = () => {
      if (users.length === 0) return
      const headers = ['ID', 'Name', 'Email', 'First Name', 'Last Name', 'Role', 'Pseudonym', 'Organization']
      const rows = users.map((u) => [
        u.user_id, u.name, u.email, u.first_name, u.last_name, u.role, u.pseudonym, u.org_name || '',
      ])
      const csv = [headers.join(','), ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `users_${new Date().toISOString().slice(0, 10)}.csv`
      a.click(); URL.revokeObjectURL(url)
    }

    const openDetail = async (userId: number) => {
      setShowDetail(true)
      setDetailLoading(true)
      setSessionsLoading(true)
      try {
        const [userRes, sessionsRes, loginHistoryRes] = await Promise.all([
          getAdminUserByIdApi(userId),
          getUserSessionsApi(userId),
          getAdminUserLoginHistoryApi(userId, { limit: 20 }),
        ])
        setDetailUser(userRes.user)
        setRoleDraft(userRes.user.role)
        setSessions(sessionsRes.sessions)
        setDevices(sessionsRes.devices)
        setActiveSessions(sessionsRes.active_sessions)
        setLoginHistory(loginHistoryRes.data)
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to load user details',
        )
        setShowDetail(false)
      } finally {
        setDetailLoading(false)
        setSessionsLoading(false)
      }
    }

    const closeDetail = () => {
      setShowDetail(false)
      setDetailUser(null)
      setRoleDraft('')
      setConfirmSuspend(false)
      setConfirmDelete(false)
      setSessions([])
      setDevices([])
      setActiveSessions(0)
      setLoginHistory([])
    }

    const isDeleted = detailUser?.deleted_at != null
    const isSuspended = detailUser?.suspended_at != null

    const handleRoleSave = async () => {
      if (!detailUser || !roleDraft || roleDraft === detailUser.role || isDeleted) return

      setRoleSaving(true)
      try {
        await updateAdminUserApi(detailUser.user_id, { role: roleDraft })
        setDetailUser((current) => current ? { ...current, role: roleDraft } : current)
        setUsers((prev) => prev.map((user) =>
          user.user_id === detailUser.user_id ? { ...user, role: roleDraft } : user,
        ))
        toast.success(`"${detailUser.name}" is now ${getRoleStyle(roleDraft).label}.`)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update user role')
      } finally {
        setRoleSaving(false)
      }
    }

    const handleSuspendToggle = async () => {
      if (!detailUser) return

      setSuspending(true)
      setConfirmSuspend(false)

      try {
        await updateAdminUserApi(detailUser.user_id, {
          suspended: !isSuspended,
        })

        // Update local state
        setDetailUser({
          ...detailUser,
          suspended_at: isSuspended ? null : new Date().toISOString(),
        })

        // Also update the user in the list cache so the badge refreshes
        setUsers((prev) =>
          prev.map((u) =>
            u.user_id === detailUser.user_id
              ? { ...u, suspended_at: isSuspended ? null : new Date().toISOString() }
              : u,
          ),
        )

        toast.success(
          isSuspended
            ? `"${detailUser.name}" has been reactivated`
            : `"${detailUser.name}" has been suspended`,
        )
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to update user status',
        )
      } finally {
        setSuspending(false)
      }
    }

    const handleRevokeSession = async (sessionId: number) => {
      if (!detailUser) return

      setRevokingSessionId(sessionId)

      try {
        await revokeUserSessionApi(detailUser.user_id, sessionId)

        // Update local state: mark the session as revoked
        setSessions((prev) =>
          prev.map((s) =>
            s.id === sessionId
              ? { ...s, status: 'revoked', revoked: true }
              : s,
          ),
        )
        setActiveSessions((prev) => Math.max(0, prev - 1))

        toast.success('Session revoked successfully.')
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to revoke session',
        )
      } finally {
        setRevokingSessionId(null)
      }
    }

    // --------------- Loading state ---------------

    if (loading && users.length === 0) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0"
                style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
              />
              <div>
                <h1 className="text-2xl font-semibold">User management</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Manage platform users and their roles.
                </p>
              </div>
            </div>
          </div>
          <RenderFilters
            showFilters={showFilters}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            roleFilter={roleFilter}
            setRoleFilter={setRoleFilter}
            orgIdFilter={orgIdFilter}
            setOrgIdFilter={setOrgIdFilter}
            onSearch={handleSearch}
            onKeyDown={handleKeyDown}
            onClear={clearFilters}
            hasFilters={hasFilters}
            showSearch={true}
          />
          <SkeletonCard count={5} />
        </div>
      )
    }

    // --------------- Empty state ---------------

    if (users.length === 0 && !loading) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0"
                style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
              />
              <div>
                <h1 className="text-2xl font-semibold">User management</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Manage platform users and their roles.
                </p>
              </div>
            </div>
          </div>

          {/* Search + filter bar (visible even when empty) */}
          <RenderFilters
            showFilters={showFilters}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            roleFilter={roleFilter}
            setRoleFilter={setRoleFilter}
            orgIdFilter={orgIdFilter}
            setOrgIdFilter={setOrgIdFilter}
            onSearch={handleSearch}
            onKeyDown={handleKeyDown}
            onClear={clearFilters}
            hasFilters={hasFilters}
            showSearch={true}
          />

          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <UsersIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No users found</p>
            <p className="text-sm text-muted-foreground">
              {hasFilters
                ? 'No users match the current search or filters.'
                : 'No users have been registered on the platform yet.'}
            </p>
            {hasFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters} className="mt-2 gap-1.5">
                <XIcon className="size-4" />
                Clear filters
              </Button>
            )}
          </div>
        </div>
      )
    }

    // --------------- Main list ---------------

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div className="flex flex-1 items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold">User management</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Manage platform users and their roles.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {selectedIds.size > 0 && (
                  <span className="text-xs font-medium text-muted-foreground">
                    {selectedIds.size} selected
                  </span>
                )}
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="flex size-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={selectedIds.size === users.length ? 'Deselect all' : 'Select all'}
                  title={selectedIds.size === users.length ? 'Deselect all' : 'Select all'}
                >
                  {selectedIds.size === users.length && users.length > 0 ? (
                    <CheckSquareIcon className="size-5" />
                  ) : (
                    <SquareIcon className="size-5" />
                  )}
                </button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportCSV}
                  className="gap-1.5"
                >
                  <DownloadIcon className="size-4" />Export CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="gap-1.5"
                >
                  <FilterIcon className="size-4" />
                  {showFilters ? 'Hide filters' : 'Filters'}
                  {hasFilters && (
                    <span className="flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                      !
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3">
            <AlertTriangleIcon className="size-4 shrink-0 text-amber-600" />
            <span className="flex-1 text-sm font-medium text-amber-700 dark:text-amber-400">
              {selectedIds.size} user{selectedIds.size !== 1 ? 's' : ''} selected
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmBulkSuspend(true)}
              disabled={bulkSuspending}
              className="gap-1.5 border-amber-500/30 text-amber-700 hover:bg-amber-500/10 dark:text-amber-400"
            >
              <BanIcon className="size-4" />
              Suspend {selectedIds.size > 1 ? `(${selectedIds.size})` : ''}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmBulkDelete(true)}
              disabled={bulkDeleting}
              className="gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10"
            >
              <Trash2Icon className="size-4" />
              Delete {selectedIds.size > 1 ? `(${selectedIds.size})` : ''}
            </Button>
          </div>
        )}
        <RenderFilters
          showFilters={showFilters}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          roleFilter={roleFilter}
          setRoleFilter={setRoleFilter}
          orgIdFilter={orgIdFilter}
          setOrgIdFilter={setOrgIdFilter}
          onSearch={handleSearch}
          onKeyDown={handleKeyDown}
          onClear={clearFilters}
          hasFilters={hasFilters}
          showSearch={true}
        />

        {/* Results count */}
        <p className="mb-4 text-xs text-muted-foreground">
          {total} user{total !== 1 ? 's' : ''}
          {page > 1 && ` — page ${page} of ${totalPages}`}
        </p>

        {/* User list */}
        <div className="space-y-2">
          {users.map((user) => {
            const roleStyle = getRoleStyle(user.role)

            return (
              <button
                key={user.user_id}
                type="button"
                onClick={() => openDetail(user.user_id)}
                className="w-full rounded-xl border border-border bg-card p-4 text-left transition-shadow hover:shadow-sm"
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleSelect(user.user_id) }}
                    className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={selectedIds.has(user.user_id) ? 'Deselect' : 'Select'}
                  >
                    {selectedIds.has(user.user_id) ? (
                      <CheckSquareIcon className="size-5" />
                    ) : (
                      <SquareIcon className="size-5" />
                    )}
                  </button>
                  {/* Avatar */}
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {user.profile_photo_url ? (
                      <img
                        src={user.profile_photo_url}
                        alt=""
                        className="size-10 rounded-full object-cover"
                      />
                    ) : (
                      <UserIcon className="size-5" />
                    )}
                  </div>

                  {/* User info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-foreground">
                        {user.name}
                      </span>
                      <span
                        className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${roleStyle.color}`}
                      >
                        <ShieldIcon className="size-3" />
                        {roleStyle.label}
                      </span>
                    </div>

                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <MailIcon className="size-3" />
                        {user.email}
                      </span>
                      {user.org_name && (
                        <span className="inline-flex items-center gap-1">
                          <BuildingIcon className="size-3" />
                          {user.org_name}
                        </span>
                      )}
                      {user.employee_identifier && (
                        <span className="inline-flex items-center gap-1">
                          <HashIcon className="size-3" />
                          {user.employee_identifier}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <CalendarIcon className="size-3" />
                        Joined {formatDate(user.created_at ?? '')}
                      </span>
                    </div>
                  </div>

                  {/* View indicator */}
                  <EyeIcon className="mt-1 size-4 shrink-0 text-muted-foreground" />
                </div>
              </button>
            )
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="gap-1.5"
            >
              <ChevronLeftIcon className="size-4" />
              Previous
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="gap-1.5"
            >
              Next
              <ChevronRightIcon className="size-4" />
            </Button>
          </div>
        )}

        {/* ── Detail Drawer ── */}
        {showDetail && (
          <>
            {/* Backdrop */}
            <button
              type="button"
              aria-label="Close detail"
              className="fixed inset-0 z-40 bg-black/30"
              onClick={closeDetail}
            />

            {/* Drawer panel */}
            <aside
              aria-label="User details"
              className="fixed inset-y-0 right-0 z-50 w-full max-w-lg border-l border-border bg-background shadow-xl motion-safe:animate-in motion-safe:slide-in-from-right motion-safe:duration-200"
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <h2 className="text-base font-semibold text-foreground">User details</h2>
                <button
                  type="button"
                  onClick={closeDetail}
                  className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Close"
                >
                  <XIcon className="size-4" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto p-5" style={{ height: 'calc(100vh - 65px)' }}>
                {detailLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
                  </div>
                ) : detailUser ? (
                  <div className="space-y-6">
                    {/* Identity section */}
                    <div className="flex items-center gap-4">
                      <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        {detailUser.profile_photo_url ? (
                          <img
                            src={detailUser.profile_photo_url}
                            alt=""
                            className="size-14 rounded-full object-cover"
                          />
                        ) : (
                          <UserIcon className="size-6" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="truncate text-lg font-semibold text-foreground">
                          {detailUser.name}
                        </h3>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${getRoleStyle(detailUser.role).color}`}
                        >
                          <ShieldIcon className="size-3" />
                          {getRoleStyle(detailUser.role).label}
                        </span>
                      </div>
                    </div>

                    {/* Field list */}
                    <div className="space-y-4">
                      <DetailField
                        icon={<MailIcon className="size-4" />}
                        label="Email"
                        value={detailUser.email}
                      />
                      <DetailField
                        icon={<UserIcon className="size-4" />}
                        label="Pseudonym"
                        value={detailUser.pseudonym}
                      />
                      <DetailField
                        icon={<BuildingIcon className="size-4" />}
                        label="Organization"
                        value={detailUser.org_name || '—'}
                      />
                      <DetailField
                        icon={<HashIcon className="size-4" />}
                        label="Org ID"
                        value={detailUser.org_id != null ? String(detailUser.org_id) : '—'}
                      />
                      <DetailField
                        icon={<HashIcon className="size-4" />}
                        label="Employee identifier"
                        value={detailUser.employee_identifier || '—'}
                      />
                      <DetailField
                        icon={<CalendarIcon className="size-4" />}
                        label="Joined"
                        value={formatDate(detailUser.created_at ?? '')}
                      />
                      <DetailField
                        icon={<ClockIcon className="size-4" />}
                        label="Last updated"
                        value={formatTimestamp(detailUser.updated_at ?? '')}
                      />
                      {isSuspended && (
                        <DetailField
                          icon={<BanIcon className="size-4 text-destructive" />}
                          label="Suspended"
                          value={detailUser.suspended_at ? formatTimestamp(detailUser.suspended_at ?? '') : 'Yes'}
                        />
                      )}
                    </div>

                    {/* ── Role editor ── */}
                    <div className="border-t border-border pt-4">
                      <label
                        htmlFor="user-role"
                        className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground/60"
                      >
                        Role
                      </label>
                      <div className="flex items-center gap-2">
                        <select
                          id="user-role"
                          aria-label="User role"
                          value={roleDraft}
                          onChange={(event) => setRoleDraft(event.target.value)}
                          disabled={roleSaving || isDeleted}
                          className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {ROLE_OPTIONS.map((role) => (
                            <option key={role} value={role}>
                              {getRoleStyle(role).label}
                            </option>
                          ))}
                        </select>
                        <Button
                          size="sm"
                          onClick={handleRoleSave}
                          disabled={roleSaving || isDeleted || !roleDraft || roleDraft === detailUser.role}
                          className="shrink-0 gap-1.5"
                        >
                          {roleSaving && <LoaderIcon className="size-4 animate-spin" />}
                          Save role
                        </Button>
                      </div>
                      <p className="mt-1.5 text-[11px] text-muted-foreground">
                        Changes take effect immediately and are recorded in the audit log.
                      </p>
                    </div>

                    {/* ── Suspend / Reactivate button ── */}
                    <div className="border-t border-border pt-4">
                      <Button
                        variant={isSuspended ? 'outline' : 'destructive'}
                        size="sm"
                        onClick={() => setConfirmSuspend(true)}
                        disabled={suspending || isDeleted}
                        className="w-full gap-1.5"
                      >
                        {suspending ? (
                          <LoaderIcon className="size-4 animate-spin" />
                        ) : (
                          <BanIcon className="size-4" />
                        )}
                        {isSuspended ? 'Reactivate account' : 'Suspend account'}
                      </Button>
                      <p className="mt-1.5 text-[11px] text-muted-foreground text-center">
                        {isDeleted
                          ? 'Account has been GDPR-anonymized.'
                          : isSuspended
                            ? 'User will regain access to the platform.'
                            : 'User will be blocked from accessing the platform.'}
                      </p>
                    </div>

                    {/* ── GDPR Delete button ── */}
                    {!isDeleted && (
                      <div className="border-t border-border pt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmDelete(true)}
                          disabled={deleting}
                          className="w-full gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10"
                        >
                          {deleting ? (
                            <LoaderIcon className="size-4 animate-spin" />
                          ) : (
                            <Trash2Icon className="size-4" />
                          )}
                          GDPR delete account
                        </Button>
                        <p className="mt-1.5 text-[11px] text-muted-foreground text-center">
                          Anonymizes all personal data. This action cannot be undone.
                        </p>
                      </div>
                    )}

                    {/* ── Sessions panel ── */}
                    <div className="border-t border-border pt-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                          Sessions
                        </h4>
                        {!sessionsLoading && sessions.length > 0 && (
                          <span className="text-[10px] text-muted-foreground">
                            {activeSessions} active · {sessions.length} total
                          </span>
                        )}
                      </div>

                      <div className="mt-3 space-y-2">
                        {sessionsLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <LoaderIcon className="size-4 animate-spin text-muted-foreground" />
                          </div>
                        ) : sessions.length === 0 ? (
                          <p className="py-2 text-center text-xs text-muted-foreground">
                            No sessions recorded.
                          </p>
                        ) : (                          sessions.slice(0, 10).map((session) => {
                            const isActive = session.status === 'active'
                            const isRevoking = revokingSessionId === session.id
                            return (
                              <div
                                key={session.id}
                                className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2"
                              >
                                <ActivityIcon
                                  className={`size-3.5 shrink-0 ${
                                    isActive
                                      ? 'text-emerald-500'
                                      : 'text-muted-foreground/50'
                                  }`}
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                                        isActive
                                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                          : session.status === 'revoked'
                                            ? 'bg-destructive/10 text-destructive'
                                            : 'bg-muted text-muted-foreground'
                                      }`}
                                    >
                                      {session.status}
                                    </span>
                                    {isActive && (
                                      <button
                                        type="button"
                                        onClick={() => handleRevokeSession(session.id)}
                                        disabled={isRevoking}
                                        className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
                                        title="Revoke session"
                                      >
                                        {isRevoking ? (
                                          <LoaderIcon className="size-3 animate-spin" />
                                        ) : (
                                          <LogOutIcon className="size-3" />
                                        )}
                                        Revoke
                                      </button>
                                    )}
                                  </div>
                                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                                    Issued {formatTimestamp(session.issued_at)}
                                    {isActive
                                      ? ` · Expires ${formatTimestamp(session.expires_at)}`
                                      : ` · Expired ${formatTimestamp(session.expires_at)}`
                                    }
                                  </p>
                                </div>
                              </div>
                            )
                          })
                        )}
                      </div>
                    </div>

                    {/* ── Login History panel ── */}
                    <div className="border-t border-border pt-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                          Login history
                        </h4>
                        {loginHistory.length > 0 && (
                          <span className="text-[10px] text-muted-foreground">
                            Last {loginHistory.length} attempts
                          </span>
                        )}
                      </div>

                      <div className="mt-3 space-y-2">
                        {loginHistory.length === 0 ? (
                          <p className="py-2 text-center text-xs text-muted-foreground">
                            No login history recorded.
                          </p>
                        ) : (
                          loginHistory.slice(0, 10).map((entry) => (
                            <div
                              key={entry.login_id}
                              className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2"
                            >
                              <div className={`flex size-6 shrink-0 items-center justify-center rounded-full ${
                                entry.success
                                  ? 'bg-emerald-500/10 text-emerald-600'
                                  : 'bg-destructive/10 text-destructive'
                              }`}>
                                <ActivityIcon className="size-3" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                                      entry.success
                                        ? 'bg-emerald-500/10 text-emerald-600'
                                        : 'bg-destructive/10 text-destructive'
                                    }`}
                                  >
                                    {entry.success ? 'Success' : 'Failed'}
                                  </span>
                                  {entry.failure_reason && (
                                    <span className="text-[10px] text-muted-foreground">
                                      {entry.failure_reason}
                                    </span>
                                  )}
                                </div>
                                <p className="mt-0.5 text-[11px] text-muted-foreground">
                                  {entry.ip_address || 'Unknown IP'}
                                  {entry.user_agent && (
                                    <span className="ml-1">· {entry.user_agent.replace(/Android.*?;|iPhone.*?;|iPad.*?;/g, '').trim().slice(0, 40)}</span>
                                  )}
                                </p>
                                <p className="text-[10px] text-muted-foreground/60">
                                  {formatTimestamp(entry.created_at)}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* ── Devices panel ── */}
                    {!sessionsLoading && devices.length > 0 && (
                      <div className="border-t border-border pt-4">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                          Devices
                        </h4>
                        <div className="mt-3 space-y-2">
                          {devices.map((device) => (
                            <div
                              key={device.id}
                              className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2"
                            >
                              <SmartphoneIcon className="size-3.5 shrink-0 text-muted-foreground" />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                                  <GlobeIcon className="size-3 text-muted-foreground" />
                                  {device.platform || 'Unknown platform'}
                                  {device.app_version && (
                                    <span className="text-muted-foreground/60">
                                      v{device.app_version}
                                    </span>
                                  )}
                                </div>
                                <p className="mt-0.5 text-[11px] text-muted-foreground">
                                  {device.last_seen_at
                                    ? `Last seen ${formatTimestamp(device.last_seen_at)}`
                                    : `Registered ${formatTimestamp(device.created_at)}`}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="py-16 text-center text-sm text-muted-foreground">
                    Could not load user details.
                  </p>
                )}
              </div>
            </aside>

            {/* ── Suspend confirmation dialog ── */}
            <DestructiveConfirm
              open={confirmSuspend}
              onClose={() => setConfirmSuspend(false)}
              title={isSuspended ? 'Reactivate account?' : 'Suspend account?'}
              description={
                isSuspended
                  ? `This will reactivate "${detailUser?.name}". They will be able to log in and use the platform again.`
                  : `This will suspend "${detailUser?.name}". They will not be able to access the platform until an admin reactivates them.`
              }
              confirmLabel={isSuspended ? 'Yes, reactivate' : 'Yes, suspend'}
              variant={isSuspended ? 'default' : 'destructive'}
              onConfirm={handleSuspendToggle}
              loading={suspending}
            />

            {/* ── GDPR Delete confirmation dialog ── */}
            <DestructiveConfirm
              open={confirmDelete}
              onClose={() => setConfirmDelete(false)}
              title="GDPR delete account?"
              description={`This will permanently anonymize "${detailUser?.name}". Their email, name, and password will be cleared, and the account will be marked as deleted. Audit trails are preserved. This cannot be undone. The user will lose all access to the platform.`}
              confirmLabel="Yes, delete permanently"
              onConfirm={async () => {
                if (!detailUser) return
                setDeleting(true)
                setConfirmDelete(false)
                try {
                  await deleteAdminUserApi(detailUser.user_id)
                  toast.success(`"${detailUser.name}" has been GDPR-anonymized.`)
                  setUsers((prev) => prev.filter((u) => u.user_id !== detailUser.user_id))
                  setTotal((prev) => Math.max(0, prev - 1))
                  closeDetail()
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Failed to delete user')
                } finally {
                  setDeleting(false)
                }
              }}
              loading={deleting}
            />

            {/* ── Bulk suspend confirmation ── */}
            <DestructiveConfirm
              open={confirmBulkSuspend}
              onClose={() => setConfirmBulkSuspend(false)}
              title={`Suspend ${selectedIds.size} user${selectedIds.size !== 1 ? 's' : ''}?`}
              description={`This will suspend ${selectedIds.size} selected user${selectedIds.size !== 1 ? 's' : ''}. They will not be able to access the platform until an admin reactivates them.`}
              confirmLabel={`Yes, suspend ${selectedIds.size > 1 ? `(${selectedIds.size})` : ''}`}
              onConfirm={handleBulkSuspend}
              loading={bulkSuspending}
            />

            {/* ── Bulk delete confirmation ── */}
            <DestructiveConfirm
              open={confirmBulkDelete}
              onClose={() => setConfirmBulkDelete(false)}
              title={`GDPR delete ${selectedIds.size} user${selectedIds.size !== 1 ? 's' : ''}?`}
              description={`This will permanently anonymize ${selectedIds.size} selected user${selectedIds.size !== 1 ? 's' : ''}. Their email, name, and password will be cleared. Audit trails are preserved. This cannot be undone.`}
              confirmLabel={`Yes, delete permanently ${selectedIds.size > 1 ? `(${selectedIds.size})` : ''}`}
              onConfirm={handleBulkDelete}
              loading={bulkDeleting}
            />
          </>
        )}
      </div>
    )
  },
})

// ── Detail field sub-component ────────────────────────────────────────

function DetailField({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
          {label}
        </p>
        <p className="truncate text-sm font-medium text-foreground">
          {value}
        </p>
      </div>
    </div>
  )
}

// ── Filters sub-component ────────────────────────────────────────────

function RenderFilters({
  showFilters,
  searchInput,
  setSearchInput,
  roleFilter,
  setRoleFilter,
  orgIdFilter,
  setOrgIdFilter,
  onSearch,
  onKeyDown,
  onClear,
  hasFilters,
  showSearch,
}: {
  showFilters: boolean
  searchInput: string
  setSearchInput: (v: string) => void
  roleFilter: string
  setRoleFilter: (v: string) => void
  orgIdFilter: string
  setOrgIdFilter: (v: string) => void
  onSearch: () => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onClear: () => void
  hasFilters: boolean
  showSearch: boolean
}) {
  return (
    <div className="mb-6 space-y-3">
      {/* Always-visible search bar */}
      {showSearch && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={onKeyDown}
              autoFocus
              placeholder="Search by name, email, or pseudonym…"
              className="w-full rounded-lg border border-border bg-background py-2 pl-10 pr-3 text-sm outline-none focus:border-primary"
            />
          </div>
          <Button size="sm" onClick={onSearch} className="gap-1.5">
            <SearchIcon className="size-4" />
            Search
          </Button>
          {hasFilters && (
            <Button variant="outline" size="sm" onClick={onClear} className="gap-1.5">
              <XIcon className="size-4" />
              Clear
            </Button>
          )}
        </div>
      )}

      {/* Collapsible filter panel */}
      {showFilters && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* Role filter */}
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              >
                <option value="">All roles</option>
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="company_admin">Company Admin</option>
                <option value="hr_manager">HR Manager</option>
                <option value="payroll_admin">Payroll Admin</option>
                <option value="employee">Employee</option>
              </select>
            </div>

            {/* Org ID filter */}
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Organization ID
              </label>
              <input
                type="number"
                min={1}
                value={orgIdFilter}
                onChange={(e) => setOrgIdFilter(e.target.value)}
                placeholder="e.g. 5"
                className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              />
            </div>

            {/* Empty cell for alignment */}
            <div />
          </div>
        </div>
      )}
    </div>
  )
}
